import Phaser from 'phaser';
import { BOSS_COLOR, GAME_FONT_FAMILY } from '@shared/constants';
import { Direction } from '@shared/types/entity';
import { getNPCTemplate } from '@shared/data/npcs';
import { QUEST_DEFINITIONS } from '@shared/data/quests';
import { getKingdomLabel, DEFAULT_KINGDOM } from '@shared/data/kingdoms';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 3, ...style } as T;
}

/**
 * Forma mínima de la escena para leer el `templateId` del NPC target desde el
 * room state client-side (mismo key `serverTargetId` que usa GameScene al armar
 * el panel). Deriva el Reino localmente sin pedir nada nuevo al server.
 */
interface NpcTemplateLookupScene {
  room?: {
    state?: { npcs?: { get(id: string): { templateId?: string } | undefined } };
  } | null;
}

export interface TargetPanelTarget {
  id: string;
  type: 'player' | 'npc';
  name: string;
  headId: number;
  /** bodyId del target. Usado como fallback en el slot del head cuando headId === 0. */
  bodyId: number;
  isPlayer: boolean;
  currentHp: number;
  maxHp: number;
  /** Si true, marca al target como boss (acento dorado). */
  isBoss?: boolean;
  /** Nombre de ícono (atlas UI) del tipo de NPC — no usado en el skin actual. */
  typeIconName?: string;
}

export interface TargetPanelOptions {
  scene: Phaser.Scene;
  initialX: number;
  initialY: number;
  initialWidth: number;
  initialHeight: number;
  minWidth?: number;
  minHeight?: number;
  depth?: number;
  onPositionSizeChange?: (x: number, y: number, width: number, height: number) => void;
}

// =============================================================================
// SKIN basado en assets/ui/target/ (ver docs/UI_SKINNING.md).
// Frame `target_frame` (511×209 nativo) escalado a FRAME_SCALE. Todas las
// posiciones de socket/ranuras se expresan en px NATIVOS del frame y se
// multiplican por FRAME_SCALE → fáciles de tunear contra el arte.
// =============================================================================
const FRAME_NATIVE_W = 511;
const FRAME_NATIVE_H = 209;
const FRAME_SCALE = 0.5;

// Socket circular del avatar (px nativos). Centro donde el retrato queda visualmente
// centrado en el rim dorado del frame (verificado con el usuario). Acá se centran
// retrato, ícono, máscara y bisel (target_avatar_overlay).
const SOCKET_CX = 105;
const SOCKET_CY = 101;
const HEAD_FIT = 68; // tamaño máx del CONTENIDO del retrato head/body (nativo)
const AVATAR_MASK_R = 56; // radio de la máscara circular del retrato
// Bisel circular (target_avatar_overlay): se centra en el centro REAL del hueco del frame
// (más abajo que el socket del retrato) y un poco más grande, para que su anillo calce con
// el rim dorado y NO se vea el borde del anillo dentro del círculo.
const AVATAR_OVERLAY_CX = 104;
const AVATAR_OVERLAY_CY = 108;
const AVATAR_R = 66; // radio (nativo) del bisel
const AVATAR_OFFSET_X = 4; // nudge fino del retrato (nativo, + = derecha)
const AVATAR_OFFSET_Y = 0; // nudge fino del retrato (nativo, + = abajo)

// Nombre (ranura superior).
const BAR_LEFT = 182;
const NAME_CY = 72;

// Barra de HP (ranura inferior). Se escala por el MISMO factor que el frame
// (FRAME_SCALE, uniforme) → no queda chica ni squishada. El fill (259×25) a 0.5
// ocupa el ancho de la ranura; se posiciona por su borde izquierdo en HP_LEFT.
const HP_LEFT = 176; // borde izquierdo del fill (nativo); termina en 176+259=435, antes de la flecha
const HP_CY = 110;

/** Texture key de la barra de HP roja (asset `target_fill_red.png`). */
const RED_FILL_KEY = 'target_fill_red';

export class TargetPanel {
  private readonly scene: Phaser.Scene;
  private readonly depth: number;
  private readonly onPositionSizeChange?: (x: number, y: number, width: number, height: number) => void;

  private x: number;
  private y: number;
  private width: number;
  private height: number;

  private visible = false;
  private currentTarget: TargetPanelTarget | null = null;
  private isPlayer = false;
  private currentIsBoss = false;

  // Drag state (sin resize: el frame es un bitmap de tamaño fijo).
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  // UI elements
  private frame: Phaser.GameObjects.Image | null = null;
  private headSprite: Phaser.GameObjects.Sprite | null = null;
  /** Bisel circular (target_avatar_overlay) ENCIMA del retrato → enmarca/viñetea el avatar. */
  private avatarOverlay: Phaser.GameObjects.Image | null = null;
  /** Graphics usado como máscara circular del avatar (head + ícono de NPC). */
  private avatarMaskGfx: Phaser.GameObjects.Graphics | null = null;
  /** Offset (display px) del centro de contenido del retrato respecto del socket. */
  private headOffX = 0;
  private headOffY = 0;
  /** Cache de bbox opaco por textura+frame. */
  private bboxCache: Map<string, { cx: number; cy: number; w: number; h: number; fw: number; fh: number }> = new Map();
  private nameText: Phaser.GameObjects.Text | null = null;
  private bossLabel: Phaser.GameObjects.Text | null = null;
  /** Etiqueta discreta con el Reino de criatura del NPC (solo NPCs). */
  private kingdomText: Phaser.GameObjects.Text | null = null;
  private hpFill: Phaser.GameObjects.Image | null = null;
  private hpText: Phaser.GameObjects.Text | null = null;
  /** Panel colapsable de características del NPC (chevron ◄/▼ + listado con iconos). */
  private detailChevron: Phaser.GameObjects.Text | null = null;
  private detailBg: Phaser.GameObjects.Rectangle | null = null;
  private detailText: Phaser.GameObjects.Text | null = null;
  private detailExpanded = false;

  constructor(opts: TargetPanelOptions) {
    this.scene = opts.scene;
    this.depth = opts.depth ?? 1050;
    this.x = opts.initialX;
    this.y = opts.initialY;
    this.width = FRAME_NATIVE_W * FRAME_SCALE;
    this.height = FRAME_NATIVE_H * FRAME_SCALE;
    this.onPositionSizeChange = opts.onPositionSizeChange;

    this.buildUI();
    this.applyVisibility(false);
  }

  // === PUBLIC API ===========================================================

  show(target: TargetPanelTarget): void {
    this.currentTarget = target;
    this.isPlayer = target.isPlayer;
    this.currentIsBoss = target.isBoss === true;
    this.refreshHead(target.headId, target.bodyId);
    this.nameText?.setText(target.name || (target.isPlayer ? 'Jugador' : 'NPC'));
    this.nameText?.setColor(this.currentIsBoss ? BOSS_COLOR : '#ffffff');
    this.updateKingdomLabel(target);
    // Panel colapsable de características (solo NPCs): arma el listado y arranca colapsado.
    this.detailExpanded = false;
    this.detailChevron?.setText('◀');
    if (target.type === 'npc') this.buildDetailForNpc(target.id);
    this.setHp(target.currentHp, target.maxHp);
    this.visible = true;
    this.applyVisibility(true);
    this.layout();
  }

  hide(): void {
    this.visible = false;
    this.currentTarget = null;
    this.applyVisibility(false);
  }

  isVisible(): boolean {
    return this.visible;
  }

  setHp(current: number, max: number): void {
    // Visibilidad de HP = reglas actuales: sólo players.
    if (!this.isPlayer) {
      this.hpFill?.setVisible(false);
      this.hpText?.setVisible(false);
      return;
    }
    this.hpFill?.setVisible(this.visible);
    this.hpText?.setVisible(this.visible);

    const safeMax = Math.max(1, max);
    const clamped = Math.max(0, Math.min(current, safeMax));
    const frac = clamped / safeMax;
    this.applyHpCrop(frac);
    this.hpText?.setText(`${Math.floor(clamped)} / ${Math.floor(safeMax)}`);
  }

  /** Estado visual de target muerto: tinta gris el retrato y la barra. */
  setHeadCasper(): void {
    if (this.headSprite) {
      this.headSprite.setTint(0x666666);
      this.headSprite.setAlpha(0.6);
    }
    this.hpFill?.setTint(0x444444);
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.visible) return false;
    return pointer.x >= this.x
      && pointer.x <= this.x + this.width
      && pointer.y >= this.y
      && pointer.y <= this.y + this.height;
  }

  isDraggingOrResizing(): boolean {
    return this.isDragging;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging) return;
    const { width: screenW, height: screenH } = this.scene.scale;
    const rawX = pointer.x - this.dragOffsetX;
    const rawY = pointer.y - this.dragOffsetY;
    this.x = Phaser.Math.Clamp(rawX, 0, Math.max(0, screenW - this.width));
    this.y = Phaser.Math.Clamp(rawY, 0, Math.max(0, screenH - this.height));
    this.layout();
    this.notifyChange();
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  getState(): { x: number; y: number; width: number; height: number } {
    return { x: this.x, y: this.y, width: this.width, height: this.height };
  }

  destroy(): void {
    this.frame?.destroy();
    this.headSprite?.destroy();
    this.avatarOverlay?.destroy();
    this.avatarMaskGfx?.destroy();
    this.nameText?.destroy();
    this.bossLabel?.destroy();
    this.kingdomText?.destroy();
    this.hpFill?.destroy();
    this.hpText?.destroy();
  }

  // === INTERNAL =============================================================

  private buildUI(): void {
    this.frame = this.scene.add
      .image(this.x, this.y, 'target_frame')
      .setOrigin(0, 0)
      .setScale(FRAME_SCALE)
      .setDepth(this.depth)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: false });

    // Drag desde el frame.
    this.frame.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - this.x;
      this.dragOffsetY = pointer.y - this.y;
      pointer.event.stopPropagation();
    });

    // Retrato (head/body) en el socket — debajo del overlay.
    this.headSprite = this.scene.add
      .sprite(0, 0, getBodySpriteKey(1))
      .setOrigin(0.5)
      .setDepth(this.depth + 1)
      .setScrollFactor(0)
      .setVisible(false);

    // Bisel circular del avatar (ENCIMA del retrato): anillo oscuro con centro
    // transparente que enmarca/viñetea el retrato dentro del hueco del frame.
    if (this.scene.textures.exists('target_avatar_overlay')) {
      this.avatarOverlay = this.scene.add
        .image(0, 0, 'target_avatar_overlay')
        .setOrigin(0.5)
        .setDepth(this.depth + 1.5)
        .setScrollFactor(0)
        .setVisible(false);
    }

    // HP fill (rojo), origen izquierdo para recortar desde la derecha.
    if (this.scene.textures.exists(RED_FILL_KEY)) {
      this.hpFill = this.scene.add
        .image(0, 0, RED_FILL_KEY)
        .setOrigin(0, 0.5)
        .setDepth(this.depth + 1)
        .setScrollFactor(0);
    }

    // Nombre (ranura superior).
    this.nameText = this.scene.add
      .text(0, 0, '', withGameFont({ fontSize: '20px', color: '#ffffff', fontStyle: 'bold' }))
      .setOrigin(0, 0.5)
      .setDepth(this.depth + 2)
      .setScrollFactor(0);

    // Label BOSS (encima del nombre).
    this.bossLabel = this.scene.add
      .text(0, 0, 'BOSS', withGameFont({ fontSize: '16px', color: BOSS_COLOR, fontStyle: 'bold' }))
      .setOrigin(0, 1)
      .setDepth(this.depth + 2)
      .setScrollFactor(0)
      .setVisible(false);

    // Reino de criatura (ranura inferior, solo NPCs — donde iría la barra de HP,
    // que para NPCs está oculta). Tono parchment tenue para que sea discreto.
    this.kingdomText = this.scene.add
      .text(0, 0, '', withGameFont({ fontSize: '18px', color: '#c9b98f' }))
      .setOrigin(0, 0.5)
      .setDepth(this.depth + 2)
      .setScrollFactor(0)
      .setVisible(false);

    // Texto de HP: mismo look que los números de hotkey del hotbar (SpellQuickBar.keyHint):
    // 12px bold, color KEYCAP_TEXT (#ece6d6), resolution 3. + stroke negro fino porque va
    // sobre la barra roja (las hotkeys van sobre keycaps oscuros y no lo necesitan).
    this.hpText = this.scene.add
      .text(0, 0, '', withGameFont({
        fontSize: '18px',
        color: '#ece6d6',
        fontStyle: 'bold',
        resolution: 3,
        stroke: '#000000',
        strokeThickness: 3,
      }))
      .setOrigin(0.5)
      .setDepth(this.depth + 3)
      .setScrollFactor(0);

    // Máscara circular del avatar: recorta el retrato al círculo del socket (no se
    // escapa del contenedor). El graphics no se renderiza (sólo es stencil).
    this.avatarMaskGfx = this.scene.add.graphics().setScrollFactor(0).setVisible(false);
    const mask = this.avatarMaskGfx.createGeometryMask();
    this.headSprite.setMask(mask);

    // Panel colapsable de características (solo NPCs). Fondo + listado con iconos, y un
    // chevron que lo despliega con slide-down. Ver docs/TARGETING.md.
    this.detailBg = this.scene.add
      .rectangle(0, 0, 10, 10, 0x0e0c09, 0.92)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x6b5a2e, 1)
      .setDepth(this.depth + 1)
      .setScrollFactor(0)
      .setVisible(false);
    this.detailText = this.scene.add
      .text(0, 0, '', withGameFont({ fontSize: '18px', color: '#d8cba8', lineSpacing: 3 }))
      .setOrigin(0, 0)
      .setDepth(this.depth + 2)
      .setScrollFactor(0)
      .setVisible(false);
    this.detailChevron = this.scene.add
      .text(0, 0, '◀', withGameFont({ fontSize: '24px', color: '#c9b98f' }))
      .setOrigin(0.5)
      .setDepth(this.depth + 3)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    this.detailChevron.on('pointerup', () => this.toggleDetail());
  }

  /** Alterna el panel colapsable de características con un slide-down. */
  private toggleDetail(): void {
    this.detailExpanded = !this.detailExpanded;
    this.detailChevron?.setText(this.detailExpanded ? '▼' : '◀');
    if (this.detailExpanded && this.detailBg && this.detailText) {
      this.detailBg.setVisible(true);
      this.detailText.setVisible(true).setAlpha(0);
      const fullH = this.detailBg.height;
      this.detailBg.setScale(1, 0);
      this.scene.tweens.add({ targets: this.detailBg, scaleY: 1, duration: 160, ease: 'Quad.easeOut' });
      this.scene.tweens.add({ targets: this.detailText, alpha: 1, duration: 220, delay: 80 });
      void fullH;
    } else {
      this.detailBg?.setVisible(false);
      this.detailText?.setVisible(false);
    }
  }

  /** Arma el listado de características del NPC target desde su template (client-side). */
  private buildDetailForNpc(npcId: string): void {
    if (!this.detailText || !this.detailBg) return;
    const templateId = this.getNpcTemplateId(npcId);
    const tpl = templateId ? getNPCTemplate(templateId) : undefined;
    if (!tpl) {
      this.detailText.setText('');
      return;
    }
    const kingdom = tpl.kingdom ?? DEFAULT_KINGDOM;
    const lines = [
      `❤ Vida  ${tpl.maxHp ?? this.currentTarget?.maxHp ?? '?'}`,
      `⚔ Daño  ${tpl.minDamage ?? 1}-${tpl.maxDamage ?? 5}`,
      `⛨ Def. física  ${tpl.physDef ?? 0}`,
      `✨ Def. mágica  ${tpl.magicDef ?? 0}`,
      `○ Evasión  ${Math.round((tpl.evasion ?? 0) * 100)}%`,
      `⚡ Vel. ataque  ${(tpl.attackSpeed ?? 1).toFixed(2)}`,
      `♛ Reino  ${getKingdomLabel(kingdom)}`,
    ];
    this.detailText.setText(lines.join('\n'));
    // Redimensionar el fondo al contenido (+ padding).
    const pad = 8;
    this.detailBg.setSize(this.detailText.width + pad * 2, this.detailText.height + pad * 2);
  }

  private refreshHead(headId: number, bodyId: number): void {
    if (!this.headSprite) return;
    this.headSprite.clearTint();
    this.headSprite.setAlpha(1);

    // Escala + centra por el CONTENIDO opaco (el frame tiene padding transparente,
    // así que centrar/escalar por el frame deja la cara chica y descentrada).
    const fitByContent = (textureKey: string, frameIndex: number): void => {
      const bb = this.getContentBBox(textureKey, frameIndex);
      const scale = bb
        ? (HEAD_FIT * FRAME_SCALE) / Math.max(1, Math.max(bb.w, bb.h))
        : (HEAD_FIT * FRAME_SCALE) / Math.max(1, this.headSprite!.height);
      this.headSprite!.setScale(scale);
      // Centrar por la CABEZA (parte superior del contenido), no por el bbox completo:
      // el contenido incluye pelo/capa/cuerpo que baja, lo que dejaba la CARA alta.
      // Tratamos la cabeza como un cuadrado de lado=ancho del contenido, arriba.
      if (bb) {
        const top = bb.cy - bb.h / 2; // y0 del contenido
        const faceCy = top + Math.min(bb.w, bb.h) / 2; // centro de la "cabeza" superior
        this.headOffX = (bb.cx - bb.fw / 2) * scale;
        this.headOffY = (faceCy - bb.fh / 2) * scale;
      } else {
        this.headOffX = 0;
        this.headOffY = 0;
      }
    };

    if (headId > 0 && this.scene.textures.exists(getHeadSpriteKey(headId))) {
      const frame = getHeadFrameForDirection(Direction.DOWN);
      this.headSprite.setTexture(getHeadSpriteKey(headId), frame);
      this.headSprite.setVisible(true);
      fitByContent(getHeadSpriteKey(headId), frame);
      return;
    }
    if (bodyId > 0 && this.scene.textures.exists(getBodySpriteKey(bodyId))) {
      const frame = getBodyIdleFrame(Direction.DOWN, bodyId);
      this.headSprite.setTexture(getBodySpriteKey(bodyId), frame);
      this.headSprite.setVisible(true);
      fitByContent(getBodySpriteKey(bodyId), frame);
      return;
    }
    this.headSprite.setVisible(false);
  }

  /**
   * Setea el SUBTÍTULO del NPC en el target: para NPCs neutrales de rol muestra el TIPO
   * (Vendedor / Hechicero / Sacerdote / Reclutador / Misiones); para hostiles (mobs) muestra
   * su Reino de criatura (su "tipo"). Todo client-side (template leído del room state). Para
   * players limpia el texto.
   */
  private updateKingdomLabel(target: TargetPanelTarget): void {
    if (!this.kingdomText) return;
    if (target.type !== 'npc') {
      this.kingdomText.setText('');
      return;
    }
    const tmpl = this.getNpcTemplateId(target.id);
    const templateDef = tmpl ? getNPCTemplate(tmpl) : undefined;
    this.kingdomText.setText(this.npcSubtitle(templateDef, tmpl));
  }

  /**
   * Etiqueta de tipo del NPC para el subtítulo del target. Neutrales de rol → su rol en
   * español; hostiles (mobs) → su Reino de criatura; genérico sin rol → vacío.
   */
  private npcSubtitle(
    tpl: ReturnType<typeof getNPCTemplate>,
    templateId: string | undefined
  ): string {
    if (!tpl) return '';
    if (tpl.hostile) return getKingdomLabel(tpl.kingdom ?? DEFAULT_KINGDOM);
    if (tpl.spellVendorId) return 'Hechicero';
    if (tpl.merchantId) return 'Vendedor';
    if (tpl.priestService) return 'Sacerdote';
    if (tpl.factionRecruiter) return 'Reclutador';
    if (templateId && TargetPanel.isQuestGiver(templateId)) return 'Misiones';
    return '';
  }

  /** True si el template inicia o recibe alguna quest (rol "Misiones"). */
  private static isQuestGiver(templateId: string): boolean {
    for (const def of Object.values(QUEST_DEFINITIONS)) {
      if (def.giverNpcId === templateId || def.turnInNpcId === templateId) return true;
    }
    return false;
  }

  /** Resuelve el templateId del NPC desde el room state (client-side, mismo key que GameScene). */
  private getNpcTemplateId(npcId: string): string | undefined {
    const scene = this.scene as unknown as NpcTemplateLookupScene;
    return scene.room?.state?.npcs?.get(npcId)?.templateId;
  }

  /**
   * Bbox del contenido opaco de un frame (en px del frame), cacheado. Permite
   * centrar/escalar el retrato por su contenido real (no por el frame con padding).
   */
  private getContentBBox(
    textureKey: string,
    frameIndex: number
  ): { cx: number; cy: number; w: number; h: number; fw: number; fh: number } | null {
    const cacheKey = `${textureKey}#${frameIndex}`;
    const cached = this.bboxCache.get(cacheKey);
    if (cached) return cached;
    const tex = this.scene.textures.get(textureKey);
    const frame = tex.get(frameIndex);
    if (!frame) return null;
    const fw = frame.cutWidth;
    const fh = frame.cutHeight;
    const canvas = document.createElement('canvas');
    canvas.width = fw;
    canvas.height = fh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const src = tex.getSourceImage() as CanvasImageSource;
    ctx.drawImage(src, frame.cutX, frame.cutY, fw, fh, 0, 0, fw, fh);
    const data = ctx.getImageData(0, 0, fw, fh).data;
    let x0 = fw, y0 = fh, x1 = -1, y1 = -1;
    for (let y = 0; y < fh; y++) {
      for (let x = 0; x < fw; x++) {
        if (data[(y * fw + x) * 4 + 3] > 20) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    const res =
      x1 < 0
        ? { cx: fw / 2, cy: fh / 2, w: fw, h: fh, fw, fh }
        : { cx: (x0 + x1 + 1) / 2, cy: (y0 + y1 + 1) / 2, w: x1 - x0 + 1, h: y1 - y0 + 1, fw, fh };
    this.bboxCache.set(cacheKey, res);
    return res;
  }

  /** Recorta la barra de HP roja a la fracción `frac` (0..1). */
  private applyHpCrop(frac: number): void {
    if (!this.hpFill) return;
    const tex = this.hpFill.texture;
    const srcW = tex.getSourceImage().width;
    const srcH = tex.getSourceImage().height;
    this.hpFill.setCrop(0, 0, Math.max(0, Math.round(srcW * frac)), srcH);
    this.hpFill.setVisible(this.visible && this.isPlayer && frac > 0);
  }

  private layout(): void {
    if (!this.frame) return;

    const { width: screenW, height: screenH } = this.scene.scale;
    this.width = FRAME_NATIVE_W * FRAME_SCALE;
    this.height = FRAME_NATIVE_H * FRAME_SCALE;
    this.x = Phaser.Math.Clamp(this.x, 0, Math.max(0, screenW - this.width));
    this.y = Phaser.Math.Clamp(this.y, 0, Math.max(0, screenH - this.height));

    this.frame.setPosition(this.x, this.y);

    const S = FRAME_SCALE;
    const px = (nx: number): number => this.x + nx * S;
    const py = (ny: number): number => this.y + ny * S;

    // Avatar
    const socketX = px(SOCKET_CX);
    const socketY = py(SOCKET_CY);
    // Máscara circular del avatar (sigue al socket en cada layout/drag).
    if (this.avatarMaskGfx) {
      this.avatarMaskGfx.clear();
      this.avatarMaskGfx.fillStyle(0xffffff, 1);
      this.avatarMaskGfx.fillCircle(socketX, socketY, AVATAR_MASK_R * S);
    }
    // Centrar la CABEZA del retrato en el socket (+ nudge fino tuneable).
    this.headSprite?.setPosition(
      socketX - this.headOffX + AVATAR_OFFSET_X * S,
      socketY - this.headOffY + AVATAR_OFFSET_Y * S,
    );
    // Bisel circular: centro PROPIO (centro real del hueco, no el del retrato) + diámetro 2·AVATAR_R.
    if (this.avatarOverlay) {
      this.avatarOverlay.setPosition(px(AVATAR_OVERLAY_CX), py(AVATAR_OVERLAY_CY));
      this.avatarOverlay.setDisplaySize(AVATAR_R * 2 * S, AVATAR_R * 2 * S);
    }

    // Nombre (ranura superior, alineado a la izquierda de la ranura). +4 = 6-2 (2px izq), +2 (2px abajo).
    this.nameText?.setPosition(px(BAR_LEFT) + 4, py(NAME_CY) + 2);
    this.bossLabel?.setPosition(px(BAR_LEFT) + 4, py(NAME_CY) - 9 + 2);

    // Reino (ranura inferior para NPCs), alineado bajo el nombre.
    this.kingdomText?.setPosition(px(BAR_LEFT) + 4, py(HP_CY));

    // HP bar (ranura inferior). Escala UNIFORME = FRAME_SCALE (igual que el frame).
    const hpLeftX = px(HP_LEFT);
    if (this.hpFill) {
      const src = this.hpFill.texture.getSourceImage();
      this.hpFill.setScale(S);
      this.hpFill.setPosition(hpLeftX, py(HP_CY));
      if (this.currentTarget) {
        const safeMax = Math.max(1, this.currentTarget.maxHp);
        const frac = Math.max(0, Math.min(this.currentTarget.currentHp, safeMax)) / safeMax;
        this.applyHpCrop(frac);
      }
      // Texto centrado sobre el ancho completo del fill. -1 = subirlo 1px (alineación fina).
      this.hpText?.setPosition(hpLeftX + (src.width * S) / 2, py(HP_CY) - 1);
    }

    // Chevron + panel colapsable de características (esquina inferior-derecha / debajo del frame).
    const isNpc = this.currentTarget?.type === 'npc';
    this.detailChevron?.setPosition(px(FRAME_NATIVE_W - 26), py(FRAME_NATIVE_H - 20));
    const detailLeft = this.x + 10;
    const detailTop = py(FRAME_NATIVE_H) - 6;
    if (this.detailBg) this.detailBg.setPosition(detailLeft, detailTop);
    if (this.detailText) this.detailText.setPosition(detailLeft + 8, detailTop + 8);
    // Visibilidad del chevron: solo NPCs. El panel se muestra sólo si está expandido.
    this.detailChevron?.setVisible(this.visible && isNpc);
    const showDetail = this.visible && isNpc && this.detailExpanded;
    this.detailBg?.setVisible(showDetail);
    this.detailText?.setVisible(showDetail);
  }

  private applyVisibility(v: boolean): void {
    this.frame?.setVisible(v);
    this.avatarOverlay?.setVisible(v);
    this.nameText?.setVisible(v);
    if (this.headSprite) {
      const hasPortrait =
        v && this.currentTarget !== null &&
        (this.currentTarget.headId > 0 || this.currentTarget.bodyId > 0);
      this.headSprite.setVisible(hasPortrait);
    }
    const showHp = v && this.isPlayer;
    this.hpFill?.setVisible(showHp);
    this.hpText?.setVisible(showHp);
    this.bossLabel?.setVisible(v && this.currentIsBoss);
    // El Reino y el panel colapsable solo aplican a NPCs.
    const isNpc = v && this.currentTarget?.type === 'npc';
    this.kingdomText?.setVisible(isNpc);
    this.detailChevron?.setVisible(isNpc);
    if (!v) {
      this.detailBg?.setVisible(false);
      this.detailText?.setVisible(false);
    }
  }

  private notifyChange(): void {
    this.onPositionSizeChange?.(this.x, this.y, this.width, this.height);
  }
}
